import { formatDateInMonthAndDateWithYear } from '@frontend/date';
import { TFunction } from '@frontend/i18n';
import { StaticVerticalStepperContent } from '@frontend/static-vertical-stepper';
import { SectionInfoProps } from '../components/common-components/sectionInfo';
import { PayerListDetails } from '../types/insurance-report-api.types';
import {
  Benefit,
  CategoryCoverage,
  Deductible,
  Maximum,
  Network,
  Service,
  TuuthFairyAPIResponse,
} from '../types/insurance-report-api.types';
import {
  constructPatientsFullName,
  converArrayToArrayOfObjects,
  convertArrayToObject,
  createInsuranceDetailsMismatchMap,
  getResponseSource,
  removeFieldsWithNoData,
  reportDateFormat,
  showDollarSign,
  titleCase,
} from './utils';

export const transformInsuranceReportApiStaticData = (
  data: TuuthFairyAPIResponse,
  payersList: PayerListDetails[],
  t: TFunction
) => {
  return {
    patientAndPayerDetails: transformPatientAndPayerDetails(data, payersList, t),
    provisions: transformProvisions(data),
    history: transformHistory(data),
  };
};

export const transformInsuranceReportApiDataForMaxAndDed = (data: TuuthFairyAPIResponse, filterValue: string) => {
  return {
    maximums: transformMaximums(data, filterValue),
    deductibles: transformDeductible(data, filterValue),
  };
};

export const transformAlternatePayerIds = (payersList: PayerListDetails[], payerId: string) => {
  const payerAlternateIds = payersList?.find((payer) => payer.id === payerId)?.alternatePayerIds || [];

  const payerAlternateIdsString = payerAlternateIds?.join(', ') || '';

  return payerAlternateIdsString;
};

export const getAlternatePayerIdSectionInfo = (
  payersList: PayerListDetails[],
  payerId: string,
  t: TFunction
): SectionInfoProps[] => {
  const alternativePayerIdsValue = transformAlternatePayerIds(payersList, payerId);

  if (!!alternativePayerIdsValue) {
    return [
      {
        label: t('Alternate Payer IDs'),
        value: alternativePayerIdsValue || '-',
      },
    ];
  }
  return [];
};

export type TransformPatientAndPayerDetailsReturnType = {
  report: SectionInfoProps[];
  printData: SectionInfoProps[];
};

export const transformPatientAndPayerDetails = (
  data: TuuthFairyAPIResponse,
  payersList: PayerListDetails[],
  t: TFunction
): TransformPatientAndPayerDetailsReturnType => {
  const { dentalSummaryInfo } = data;
  const { patient, subscriber, plan } = dentalSummaryInfo || {};

  const insuranceDetailsMismatchMap = createInsuranceDetailsMismatchMap(data?.patientMismatchDetails || []);
  const {
    PATIENT_NAME: patientNameMismatch,
    PATIENT_MEMBER_ID: patientMemberIdMismatch,
    PATIENT_DOB: patientDobMismatch,
    SUBSCRIBER_NAME: subscriberNameMismatch,
    SUBSCRIBER_MEMBER_ID: subscriberMemberIdMismatch,
    SUBSCRIBER_DOB: subscriberDobMismatch,
  } = insuranceDetailsMismatchMap;

  const patientName = constructPatientsFullName(patient?.firstName || '', patient?.lastName || '');
  const subscriberName = constructPatientsFullName(subscriber?.firstName || '', subscriber?.lastName || '');
  const patientDob = reportDateFormat(patient?.dob);
  const subscriberDob = reportDateFormat(subscriber?.dob);

  const resolvedSubscriberName = subscriberNameMismatch?.planValue || subscriberName;
  const resolvedSubscriberDob = subscriberDobMismatch?.planValue || subscriberDob;

  const isSubscriberNameAndPatientNameEqual = resolvedSubscriberName === patientName;
  const isSubscriberDOBAndPatientDOBEqual = resolvedSubscriberDob === patientDob;

  const printData: SectionInfoProps[] = [
    {
      label: t('Subscriber Name'),
      value: subscriberName,
    },
    {
      label: t('Report Type'),
      value: (getResponseSource(data?.dentalSummaryInfo?.requests) || '').split(' ')[0] || '',
    },
    {
      label: t('Report Date'),
      value: reportDateFormat(new Date().toISOString()),
    },
  ];

  const reportData = [
    {
      label: t('Patient Name'),
      value: patientNameMismatch?.planValue || patientName || '-',
      mismatchRequestValue: patientNameMismatch?.requestValue,
    },
    {
      label: t('Payer Name'),
      value: data?.dentalSummaryInfo?.plan?.payerName || '-',
    },
    {
      label: t('Payer ID'),
      value: data?.dentalSummaryInfo?.plan?.payerId || '-',
    },
    {
      label: t('Member ID'),
      value: patientMemberIdMismatch?.planValue || data?.dentalSummaryInfo?.patient?.memberId || '-',
      mismatchRequestValue: patientMemberIdMismatch?.requestValue,
    },
    {
      label: t('Group Name'),
      value: data?.dentalSummaryInfo?.plan?.groupName || '-',
    },
    {
      label: t('Group Number'),
      value: data?.dentalSummaryInfo?.plan?.groupNumber || '-',
    },
    ...getAlternatePayerIdSectionInfo(payersList, plan?.payerId || '', t),
    {
      label: t('Date of Birth'),
      value: patientDobMismatch?.planValue || patientDob,
      mismatchRequestValue: patientDobMismatch?.requestValue,
    },
    {
      label: t('Subscriber Name'),
      value: isSubscriberNameAndPatientNameEqual ? '-' : resolvedSubscriberName || '-',
      mismatchRequestValue: subscriberNameMismatch?.requestValue,
    },
    {
      label: t('Subscriber ID'),
      value: subscriberMemberIdMismatch?.planValue || subscriber?.memberId || '-',
      mismatchRequestValue: subscriberMemberIdMismatch?.requestValue,
    },
    {
      label: t('Subscriber Date of Birth'),
      value: isSubscriberDOBAndPatientDOBEqual ? '-' : resolvedSubscriberDob,
      mismatchRequestValue: subscriberDobMismatch?.requestValue,
    },
    {
      label: t('Effective Date'),
      value: reportDateFormat(data?.dentalSummaryInfo?.plan?.effectiveDate),
    },
    {
      label: t('End Date'),
      value: reportDateFormat(data?.dentalSummaryInfo?.plan.endDate),
    },
    {
      label: t('Missing Tooth Clause'),
      value: data?.dentalSummaryInfo?.plan?.hasMissingToothClause ? 'Yes' : 'No',
    },
    {
      label: t('Downgrades'),
      value: data?.dentalSummaryInfo?.plan?.hasAlternateBenefits ? 'Yes' : 'No',
    },
    {
      label: t('COB'),
      value: titleCase(data?.dentalSummaryInfo?.plan?.coordinationOfBenefits) || '-',
    },
    {
      label: t('TOA'),
      value: data?.dentalSummaryInfo?.plan?.hasTOA ? 'Yes' : 'No',
    },
    {
      label: t('Insurance Type'),
      value: data?.dentalSummaryInfo?.plan?.insuranceType || '-',
    },
    {
      label: t('Renewal Type'),
      value: titleCase(data?.dentalSummaryInfo?.plan?.renewalType || '-'),
    },
    {
      label: t('Multi Visits Paid On'),
      value: data?.dentalSummaryInfo?.plan?.multiVisitsPaidOn || '-',
    },
  ];
  const filteredData = removeFieldsWithNoData(reportData, printData);
  return filteredData;
};

const transformMaximums = (data: TuuthFairyAPIResponse, filterValue: string) => {
  const allMaximums = data?.dentalSummaryInfo?.plan?.maximums || [];
  // Dynamically accessing the filterKey property on the item
  const dentalCareCategory: Maximum[] = [],
    otherCategory: Maximum[] = [];

  allMaximums.forEach((item) => {
    if (item['networkName'] === filterValue) {
      if (item.category === 'dental_care') {
        dentalCareCategory.push(item);
      } else {
        otherCategory.push(item);
      }
    }
  });
  dentalCareCategory.sort((a, b) => (b.remaining as number) - (a.remaining as number));
  otherCategory.sort((a, b) => (b.remaining as number) - (a.remaining as number));

  return [...dentalCareCategory, ...otherCategory];
};

const transformDeductible = (data: TuuthFairyAPIResponse, filterValue: string) => {
  const allDeductibles = data?.dentalSummaryInfo?.plan?.deductibles || [];

  const dentalCareCategory: Deductible[] = [],
    otherCategory: Deductible[] = [];

  allDeductibles.forEach((item) => {
    if (item['networkName'] === filterValue) {
      if (item.category === 'dental_care') {
        dentalCareCategory.push(item);
      } else {
        otherCategory.push(item);
      }
    }
  });

  dentalCareCategory.sort((a, b) => (b.remaining as number) - (a.remaining as number));
  otherCategory.sort((a, b) => (b.remaining as number) - (a.remaining as number));
  return [...dentalCareCategory, ...otherCategory];
};

const transformProvisions = (data: TuuthFairyAPIResponse): string[] => {
  return data?.dentalSummaryInfo?.plan?.provisions || [];
};

export const groupTuuthFairyServicesDataByDate = (services: Service[]): StaticVerticalStepperContent[] => {
  const grouped = new Map<string, string[]>();

  for (const service of services || []) {
    const dateKey = formatDateInMonthAndDateWithYear(new Date(service.date));

    const { code, toothNumber, toothSurface, toothDescription, category } = service;
    const codeOrCategory = code || category;

    const toothParts: string[] = [];
    if (toothNumber) toothParts.push(`Tooth: ${toothNumber}`);
    if (toothSurface) toothParts.push(`Surface: ${toothSurface}`);
    if (toothDescription) toothParts.push(toothDescription);

    const toothInfo = toothParts.length > 0 ? ` (${toothParts.join(', ')})` : '';
    const line = `${codeOrCategory}${toothInfo}`;

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(line);
  }

  // Convert Map to sorted array
  return Array.from(grouped.entries())
    .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
    .map(([dateKey, toothData]) => ({
      title: dateKey,
      subTitle: toothData.join(', '),
    }));
};

const transformHistory = (data: TuuthFairyAPIResponse): StaticVerticalStepperContent[] => {
  const transformedHistoryData = groupTuuthFairyServicesDataByDate(data?.dentalSummaryInfo?.plan?.services || []);
  return transformedHistoryData;
};
/**
 * return value will be
 * {

 * 'Delta dental premiere': {
 *    'adjunctive': [benefit9, benefit10],
 *    'category2': [benefit11, benefit12],
 * },
 * 'out_of_network': {
 *   'adjunctive': [benefit5, benefit6],
 *   'category2': [benefit7, benefit8],
 * },
 */
export const transformFrequencyAndLimitations = (
  data: Benefit[],
  networkFilterValues: string[],
  procedureCodeServiceDates: Record<string, string[]>,
  favoriteCDTCodes: string[],
  requestCDTCodes: string[]
) => {
  const allNetworkFilters = convertArrayToObject(networkFilterValues);
  //This is specific to favorite/request CDT codes table data segregation which contains network type array of objects
  const favoriteCDTCodeNetworkFiltersData = converArrayToArrayOfObjects(networkFilterValues);
  const requestCDTCodesNetworkFiltersData = converArrayToArrayOfObjects(networkFilterValues);
  // Using Set for O(1) lookup time complexity to keep track of fetched CDT codes from the user selected/requested CDT codes
  const favoriteSet = new Set(favoriteCDTCodes);
  const requestSet = new Set(requestCDTCodes);

  const fetchedRequestCDTCodes: string[] = [];
  const fetchedFavoriteCDTCodes: string[] = [];
  const segregatedDataForAllTables = (data || []).reduce((accumulator, currentItem) => {
    const networkName = currentItem.networkName;
    const category = currentItem.category;

    // avoid mutation
    const item = {
      ...currentItem,
      serviceDates: procedureCodeServiceDates[currentItem.code] || [],
    };

    if (!accumulator[networkName]) accumulator[networkName] = {};
    if (!accumulator[networkName][category]) accumulator[networkName][category] = [];

    // Favorites
    if (favoriteSet.has(item.code)) {
      fetchedFavoriteCDTCodes.push(item.code);
      favoriteCDTCodeNetworkFiltersData[networkName].push(item);
    }

    // Requests
    if (requestSet.has(item.code)) {
      fetchedRequestCDTCodes.push(item.code);
      requestCDTCodesNetworkFiltersData[networkName].push(item);
    }

    allNetworkFilters[networkName][category].push(item);
    return accumulator;
  }, allNetworkFilters);

  // Merge request into favorite (single pass)
  for (const network in favoriteCDTCodeNetworkFiltersData) {
    const favArr = favoriteCDTCodeNetworkFiltersData[network];
    const reqArr = requestCDTCodesNetworkFiltersData[network];
    if (reqArr?.length) favArr.push(...reqArr);
  }

  const missingCDTCodes = getMissingCDTCodes(
    [...new Set([...requestCDTCodes, ...favoriteCDTCodes])],
    [...new Set([...fetchedRequestCDTCodes, ...fetchedFavoriteCDTCodes])]
  );
  addMissingCDTCodesData(missingCDTCodes, favoriteCDTCodeNetworkFiltersData);

  return { segregatedDataForAllTables, favoriteCDTCodeNetworkFiltersData };
};

const getMissingCDTCodes = (CDTCodes: string[], fetchedCDTCodes: string[]) => {
  return CDTCodes.filter((code) => !fetchedCDTCodes.includes(code));
};

const addMissingCDTCodesData = (missingCDTCodes: string[], CDTCodesNetworkFiltersData: Record<string, Benefit[]>) => {
  missingCDTCodes.forEach((code) => {
    Object.keys(CDTCodesNetworkFiltersData).forEach((network) => {
      CDTCodesNetworkFiltersData[network].push({
        code,
        description: 'Data not received from the payer',
        ageMin: 0,
        ageMax: 0,
        category: '',
        coPay: 0,
        coverage: 0,
        deductibleApplies: false,
        limit: 0,
        limitUnit: '',
        networkName: network,
        networkType: '' as any,
        notes: [],
        period: 0,
        periodUnit: '' as any,
        source: '' as any,
        waitingPeriod: 0,
        waitingPeriodUnit: '',
        serviceDates: [],
      });
    });
  });
};
export const transformCategoryCoverage = (data: CategoryCoverage[]) => {
  return (data || []).reduce((accumulator, currentItem) => {
    if (!accumulator[currentItem.networkName]) {
      accumulator[currentItem.networkName] = [];
    }

    accumulator[currentItem.networkName].push(currentItem);
    return accumulator;
  }, {} as Record<string, CategoryCoverage[]>);
};
export const getNetworkNames = (networks: Network[]) => {
  const networkNames: string[] = [];
  for (const network of networks) {
    networkNames.push(network.name);
  }
  return networkNames;
};

export const transformRemainingAndTotalValues = (data: Maximum[]): Maximum[] => {
  return data.map((item) => {
    return {
      ...item,
      remaining: showDollarSign(item.remaining),
      amount: showDollarSign(item.amount),
    };
  }) as Maximum[];
};

export function groupServiceDatesByProcedureCode(services: Service[]): Record<string, string[]> {
  const groupedByCode = services.reduce((acc, service) => {
    if (!acc[service.code]) {
      acc[service.code] = new Set<string>();
    }
    acc[service.code].add(reportDateFormat(service.date || ''));
    return acc;
  }, {} as Record<string, Set<string>>);

  // Converting Set to arrays and sort dates in descending order
  return Object.fromEntries(
    Object.entries(groupedByCode).map(([code, dates]) => [
      code,
      Array.from(dates).sort((a, b) => new Date(b).getTime() - new Date(a).getTime()),
    ])
  );
}
/**
 * Decodes a base64-encoded HTML string (as returned by the insurance eligibility report
 * encoded-HTML endpoint) into a UTF-8 string that can be rendered via `dangerouslySetInnerHTML`
 * for printing/writeback in the insurance report container.
 */
export const decodeBase64Html = (base64: string): string => {
  if (!base64) return '';
  // `atob` decodes the base64 string into a binary string where each char code is one raw byte.
  // It does NOT handle multi-byte UTF-8 on its own, which is why we re-decode below.
  const binary = atob(base64);
  // Convert the binary string into a byte array by reading each character's raw byte value.
  // This preserves the original UTF-8 byte sequence emitted by the backend.
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  // Decode the raw UTF-8 bytes into a proper JS string so non-ASCII characters in the
  // printable report (names, payer info, etc.) render correctly instead of as mojibake.
  return new TextDecoder('utf-8').decode(bytes);
};
